import { ICON_STROKE } from './chunk-ETUJHD53.js';
import { forwardRef, createContext, useState, useCallback, useMemo, useContext, useEffect, useSyncExternalStore } from 'react';
import { Loader2, X, Inbox, AlertCircle, Bot } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import * as Dialog from '@radix-ui/react-dialog';
import * as RadixTabs from '@radix-ui/react-tabs';
import * as RadixToast from '@radix-ui/react-toast';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var VARIANTS = {
  primary: "bg-[var(--color-accent)] text-[var(--color-accent-text)] hover:bg-[var(--color-accent-hover)] shadow-sm",
  secondary: "bg-[var(--color-surface-2)] text-[var(--color-text-primary)] hover:bg-[var(--color-surface-3)] border border-[var(--color-border)]",
  ghost: "bg-transparent text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)]",
  danger: "bg-[var(--color-danger)] text-white hover:opacity-90 shadow-sm"
};
var SIZES = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-6 text-base"
};
var Button = forwardRef(function Button2({
  variant = "primary",
  size = "md",
  loading = false,
  leftIcon,
  rightIcon,
  className,
  children,
  disabled,
  type = "button",
  ...rest
}, ref) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      ref,
      type,
      disabled: disabled || loading,
      "aria-busy": loading || void 0,
      className: cn(
        "inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)] font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface-1)]",
        "disabled:cursor-not-allowed disabled:opacity-50",
        VARIANTS[variant],
        SIZES[size],
        className
      ),
      ...rest,
      children: [
        loading ? /* @__PURE__ */ jsx(Loader2, { className: "h-4 w-4 animate-spin", "aria-hidden": "true" }) : leftIcon,
        children,
        !loading && rightIcon
      ]
    }
  );
});
var PAD = {
  none: "",
  sm: "p-3",
  md: "p-5",
  lg: "p-7"
};
var Card = forwardRef(function Card2({ padding = "md", hover = false, className, children, ...rest }, ref) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(
        "rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-1)]",
        "shadow-card",
        hover && "transition-shadow hover:shadow-card-hover",
        PAD[padding],
        className
      ),
      ...rest,
      children
    }
  );
});
var CardHeader = forwardRef(function CardHeader2({ className, ...rest }, ref) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn("mb-3 flex items-center justify-between gap-3", className),
      ...rest
    }
  );
});
var CardTitle = forwardRef(function CardTitle2({ className, ...rest }, ref) {
  return /* @__PURE__ */ jsx(
    "h3",
    {
      ref,
      className: cn("text-base font-semibold text-[var(--color-text-primary)]", className),
      ...rest
    }
  );
});
var SIZE = {
  sm: "w-[min(420px,90vw)]",
  md: "w-[min(560px,90vw)]",
  lg: "w-[min(720px,92vw)]",
  xl: "w-[min(960px,94vw)]"
};
function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  hideCloseButton = false,
  size = "md"
}) {
  return /* @__PURE__ */ jsx(Dialog.Root, { open, onOpenChange, children: /* @__PURE__ */ jsxs(Dialog.Portal, { children: [
    /* @__PURE__ */ jsx(Dialog.Overlay, { className: "fixed inset-0 z-50 bg-black/40 backdrop-blur-sm animate-fade-in" }),
    /* @__PURE__ */ jsxs(
      Dialog.Content,
      {
        className: cn(
          "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
          "rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-1)] p-6",
          "shadow-card-hover animate-slide-up",
          SIZE[size],
          className
        ),
        children: [
          title && /* @__PURE__ */ jsx(Dialog.Title, { className: "text-lg font-semibold text-[var(--color-text-primary)]", children: title }),
          description && /* @__PURE__ */ jsx(Dialog.Description, { className: "mt-1 text-sm text-[var(--color-text-secondary)]", children: description }),
          /* @__PURE__ */ jsx("div", { className: cn(title || description ? "mt-4" : void 0), children }),
          !hideCloseButton && /* @__PURE__ */ jsx(
            Dialog.Close,
            {
              "aria-label": "Close",
              className: cn(
                "absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-control)]",
                "text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text-primary)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
              ),
              children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
            }
          )
        ]
      }
    )
  ] }) });
}
function Tabs({
  value,
  onValueChange,
  items,
  className,
  listClassName,
  contentClassName
}) {
  return /* @__PURE__ */ jsxs(
    RadixTabs.Root,
    {
      value,
      onValueChange,
      className: cn("w-full", className),
      children: [
        /* @__PURE__ */ jsx(
          RadixTabs.List,
          {
            className: cn(
              "flex gap-1 border-b border-[var(--color-border)] px-1",
              listClassName
            ),
            children: items.map((it) => /* @__PURE__ */ jsx(
              RadixTabs.Trigger,
              {
                value: it.value,
                disabled: it.disabled,
                className: cn(
                  "relative -mb-px px-3 py-2 text-sm font-medium text-[var(--color-text-secondary)]",
                  "transition-colors hover:text-[var(--color-text-primary)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-1",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  "data-[state=active]:text-[var(--color-text-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--color-accent)]"
                ),
                children: it.label
              },
              it.value
            ))
          }
        ),
        items.map((it) => /* @__PURE__ */ jsx(
          RadixTabs.Content,
          {
            value: it.value,
            className: cn("pt-4", contentClassName),
            children: it.content
          },
          it.value
        ))
      ]
    }
  );
}
var ToastCtx = createContext(null);
var VARIANT_BORDER = {
  default: "border-[var(--color-border)]",
  success: "border-[var(--color-success)]",
  danger: "border-[var(--color-danger)]",
  warning: "border-[var(--color-warning)]"
};
var VARIANT_TITLE = {
  default: "text-[var(--color-text-primary)]",
  success: "text-[var(--color-success)]",
  danger: "text-[var(--color-danger)]",
  warning: "text-[var(--color-warning)]"
};
function generateId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `toast_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}
function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const dismiss = useCallback((id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);
  const toast = useCallback((msg, opts = {}) => {
    const id = generateId();
    setItems((prev) => [
      ...prev,
      {
        id,
        msg,
        description: opts.description,
        variant: opts.variant ?? "default",
        durationMs: opts.durationMs ?? 4e3
      }
    ]);
    return id;
  }, []);
  const api = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);
  return /* @__PURE__ */ jsx(ToastCtx.Provider, { value: api, children: /* @__PURE__ */ jsxs(RadixToast.Provider, { swipeDirection: "right", children: [
    children,
    items.map((it) => /* @__PURE__ */ jsxs(
      RadixToast.Root,
      {
        duration: it.durationMs,
        onOpenChange: (open) => {
          if (!open) dismiss(it.id);
        },
        className: cn(
          "rounded-[var(--radius-control)] border bg-[var(--color-surface-1)] p-3 shadow-card animate-toast-enter",
          VARIANT_BORDER[it.variant]
        ),
        children: [
          /* @__PURE__ */ jsx(RadixToast.Title, { className: cn("text-sm font-medium", VARIANT_TITLE[it.variant]), children: it.msg }),
          it.description && /* @__PURE__ */ jsx(RadixToast.Description, { className: "mt-1 text-xs text-[var(--color-text-secondary)]", children: it.description })
        ]
      },
      it.id
    )),
    /* @__PURE__ */ jsx(RadixToast.Viewport, { className: "fixed bottom-4 right-4 z-[100] flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none" })
  ] }) });
}
function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}
var RADIUS = {
  sm: "rounded",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full"
};
function Skeleton({ w, h = 12, radius = "md", className, style, ...rest }) {
  const size = { width: w, height: h };
  return /* @__PURE__ */ jsx(
    "div",
    {
      "aria-hidden": true,
      className: cn("skeleton-bone", RADIUS[radius], className),
      style: { ...size, ...style },
      ...rest
    }
  );
}
function SkeletonText({ lines = 3, className, ...rest }) {
  return /* @__PURE__ */ jsx("div", { "aria-hidden": true, className: cn("space-y-2", className), ...rest, children: Array.from({ length: lines }).map((_, i) => /* @__PURE__ */ jsx(
    "div",
    {
      className: "skeleton-bone h-3 rounded-md",
      style: { width: i === lines - 1 && lines > 1 ? "60%" : "100%" }
    },
    i
  )) });
}
function SkeletonRow({ className, ...rest }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "aria-hidden": true,
      className: cn("flex items-center gap-3 py-[var(--space-row-y)]", className),
      ...rest,
      children: [
        /* @__PURE__ */ jsx("div", { className: "skeleton-bone h-8 w-8 shrink-0 rounded-full" }),
        /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-2", children: [
          /* @__PURE__ */ jsx("div", { className: "skeleton-bone h-3 rounded-md", style: { width: "40%" } }),
          /* @__PURE__ */ jsx("div", { className: "skeleton-bone h-3 rounded-md", style: { width: "70%" } })
        ] })
      ]
    }
  );
}
function readViteEnv() {
  try {
    return import.meta.env ?? {};
  } catch {
    return {};
  }
}
var VITE_ENV = readViteEnv();
var TENANT = {
  name: VITE_ENV.VITE_INSTANCE_BRAND,
  logo: VITE_ENV.VITE_INSTANCE_BRAND_LOGO,
  colors: VITE_ENV.VITE_INSTANCE_BRAND_COLORS?.split(",").map((c) => c.trim())
};
function hexRgba(hex, alpha) {
  const v = hex.replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(v)) return `rgba(102,84,245,${alpha})`;
  const [r, g, b] = [v.slice(0, 2), v.slice(2, 4), v.slice(4, 6)].map((p) => parseInt(p, 16));
  return `rgba(${r},${g},${b},${alpha})`;
}
var BRAND = {
  ink: "#0b0c18",
  amber: TENANT.colors?.[0] ?? "#f2b347",
  pink: TENANT.colors?.[1] ?? "#ca5a8b",
  purple: TENANT.colors?.[2] ?? "#6654f5"
};
var DEFAULT_MESSAGES = [
  "Connecting to the nerve center",
  "Syncing your workspace",
  "Loading agent activity",
  "Bringing the surface online"
];
var PARTICLES = [
  { radius: 150, duration: 8, reverse: false, color: BRAND.amber, size: 7, delay: 0 },
  { radius: 150, duration: 8, reverse: false, color: BRAND.purple, size: 5, delay: -4 },
  { radius: 108, duration: 5.5, reverse: true, color: BRAND.pink, size: 9, delay: -1.4 },
  { radius: 192, duration: 12, reverse: false, color: BRAND.pink, size: 5, delay: -6 },
  { radius: 192, duration: 12, reverse: true, color: BRAND.amber, size: 4, delay: -2.5 }
];
function OmniLoadingScreen({
  label,
  messages = DEFAULT_MESSAGES,
  subtitle,
  fill = false
}) {
  const [messageIndex, setMessageIndex] = useState(0);
  useEffect(() => {
    if (label) return;
    const id = window.setInterval(() => {
      setMessageIndex((i) => (i + 1) % messages.length);
    }, 1900);
    return () => window.clearInterval(id);
  }, [label, messages.length]);
  const currentMessage = label ?? messages[messageIndex];
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `flex flex-col items-center justify-center overflow-hidden ${fill ? "relative min-h-full w-full flex-1 self-stretch" : "fixed inset-0 z-[100] w-full"}`,
      style: { background: BRAND.ink },
      children: [
        /* @__PURE__ */ jsxs("div", { "aria-hidden": true, className: "pointer-events-none absolute inset-0", children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "animate-omni-float absolute -top-48 -left-40 h-[680px] w-[680px] rounded-full blur-3xl",
              style: { background: hexRgba(BRAND.amber, 0.18) }
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "animate-omni-float absolute top-1/4 -right-48 h-[760px] w-[760px] rounded-full blur-3xl",
              style: { background: hexRgba(BRAND.pink, 0.2), animationDelay: "-5s" }
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "animate-omni-float absolute -bottom-56 left-1/4 h-[720px] w-[720px] rounded-full blur-3xl",
              style: { background: hexRgba(BRAND.purple, 0.22), animationDelay: "-10s" }
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "absolute inset-0 opacity-[0.04]",
              style: {
                backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)",
                backgroundSize: "34px 34px"
              }
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "absolute inset-0",
              style: {
                background: "radial-gradient(ellipse at 50% 42%, transparent 0%, transparent 38%, rgba(11,12,24,0.85) 80%)"
              }
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { "aria-hidden": true, className: "relative h-[400px] w-[400px]", children: [
          /* @__PURE__ */ jsx("div", { className: "animate-omni-spin-12 absolute inset-0", children: /* @__PURE__ */ jsx(
            "div",
            {
              className: "omni-arc h-full w-full",
              style: {
                background: `conic-gradient(from 90deg, transparent 0deg, transparent 200deg, ${BRAND.purple}00 230deg, ${BRAND.purple} 320deg, ${BRAND.pink} 360deg)`
              }
            }
          ) }),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "animate-omni-ring-breathe absolute inset-0 rounded-full border",
              style: { borderColor: hexRgba(BRAND.purple, 0.22) }
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "animate-omni-spin-rev-16 absolute inset-[52px]", children: /* @__PURE__ */ jsx(
            "div",
            {
              className: "omni-arc omni-arc-thick h-full w-full",
              style: {
                background: `conic-gradient(from 0deg, transparent 0deg, transparent 240deg, ${BRAND.amber}00 270deg, ${BRAND.amber} 340deg, ${BRAND.pink} 360deg)`
              }
            }
          ) }),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "animate-omni-ring-breathe absolute inset-[104px] rounded-full border",
              style: { borderColor: hexRgba(BRAND.pink, 0.22), animationDelay: "-1.5s" }
            }
          ),
          PARTICLES.map((p, i) => /* @__PURE__ */ jsx(
            "div",
            {
              className: `absolute left-1/2 top-1/2 ${p.reverse ? "animate-omni-spin-rev-6" : "animate-omni-spin-8"}`,
              style: { animationDuration: `${p.duration}s`, animationDelay: `${p.delay}s` },
              children: /* @__PURE__ */ jsx(
                "span",
                {
                  className: "animate-omni-particle absolute rounded-full",
                  style: {
                    width: p.size,
                    height: p.size,
                    background: p.color,
                    left: p.radius,
                    top: -p.size / 2,
                    marginLeft: -p.size / 2,
                    boxShadow: `0 0 14px ${p.color}, 0 0 5px ${p.color}`,
                    animationDelay: `${p.delay}s`
                  }
                }
              )
            },
            i
          )),
          /* @__PURE__ */ jsx("div", { className: "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2", children: /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsx(
              "span",
              {
                "aria-hidden": true,
                className: "animate-omni-core-glow absolute left-1/2 top-1/2 -z-10 h-[230px] w-[230px] -translate-x-1/2 -translate-y-1/2 rounded-full",
                style: {
                  background: `radial-gradient(circle, ${hexRgba(BRAND.purple, 0.55)} 0%, ${hexRgba(BRAND.pink, 0.3)} 42%, transparent 70%)`,
                  filter: "blur(14px)"
                }
              }
            ),
            /* @__PURE__ */ jsx(
              "img",
              {
                src: TENANT.logo ?? "/branding/edna-icon-colour.png",
                alt: TENANT.name ?? "Enterprise DNA",
                className: TENANT.logo ? "relative h-[110px] w-auto max-w-[230px] object-contain" : "relative h-[120px] w-[120px]",
                style: { filter: `drop-shadow(0 8px 40px ${hexRgba(BRAND.purple, 0.45)})` }
              }
            )
          ] }) })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "relative -mt-2 flex flex-col items-center", children: [
          TENANT.name ? /* @__PURE__ */ jsx("span", { className: "text-[26px] font-semibold tracking-tight text-white md:text-3xl", children: TENANT.name }) : /* @__PURE__ */ jsx(
            "img",
            {
              src: "/branding/edna-wordmark-white.png",
              alt: "Enterprise DNA",
              className: "h-9 w-auto md:h-11"
            }
          ),
          subtitle && /* @__PURE__ */ jsx("span", { className: "mt-4 text-[11px] font-semibold uppercase tracking-[0.52em] text-white/40", children: subtitle }),
          /* @__PURE__ */ jsx("div", { className: "mt-7 h-5 overflow-hidden", children: /* @__PURE__ */ jsxs("p", { className: "animate-fade-in-up text-sm font-medium text-white/55", children: [
            currentMessage,
            /* @__PURE__ */ jsx("span", { className: "animate-pulse-dot ml-1 text-brand-pink", children: "\u2026" })
          ] }, currentMessage) }),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "relative mt-4 h-[2px] w-64 overflow-hidden rounded-full",
              style: { background: "rgba(255,255,255,0.08)" },
              children: /* @__PURE__ */ jsx(
                "div",
                {
                  className: "animate-omni-progress absolute top-0 h-full w-1/3 rounded-full",
                  style: {
                    background: `linear-gradient(90deg, transparent, ${BRAND.amber}, ${BRAND.pink}, ${BRAND.purple}, transparent)`
                  }
                }
              )
            }
          )
        ] })
      ]
    }
  );
}
function EmptyState({ icon, title, detail, action, className }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "rounded-[var(--radius-panel)] border border-dashed border-[var(--color-border)] bg-[var(--color-surface-1)] p-8 text-center",
        className
      ),
      children: [
        icon === void 0 ? /* @__PURE__ */ jsx(Inbox, { size: 22, strokeWidth: ICON_STROKE, className: "mx-auto text-[var(--color-text-secondary)]" }) : icon && /* @__PURE__ */ jsx("span", { className: "mx-auto inline-flex text-[var(--color-text-secondary)]", children: icon }),
        /* @__PURE__ */ jsx("p", { className: "mt-3 text-sm font-medium text-[var(--color-text-primary)]", children: title }),
        detail && /* @__PURE__ */ jsx("p", { className: "mx-auto mt-1.5 max-w-md text-[13px] leading-relaxed text-[var(--color-text-secondary)]", children: detail }),
        action && /* @__PURE__ */ jsx("div", { className: "mt-4 flex justify-center", children: action })
      ]
    }
  );
}
function ErrorState({ title, detail, onRetry, className }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "rounded-[var(--radius-panel)] border border-[var(--color-danger-muted)] bg-[var(--color-danger-muted)] p-4 text-sm text-[var(--color-danger)]",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 font-medium", children: [
          /* @__PURE__ */ jsx(AlertCircle, { size: 16, strokeWidth: ICON_STROKE }),
          title
        ] }),
        detail && /* @__PURE__ */ jsx("p", { className: "mt-1 text-[var(--color-danger)]", children: detail }),
        onRetry && /* @__PURE__ */ jsx(Button, { variant: "secondary", size: "sm", onClick: onRetry, className: "mt-3", children: "Retry" })
      ]
    }
  );
}
function PageError({ message }) {
  return /* @__PURE__ */ jsx(ErrorState, { title: "Unable to load data", detail: message });
}
function PageLoading({ label = "Loading", subtitle }) {
  return /* @__PURE__ */ jsx(OmniLoadingScreen, { label, subtitle });
}
function PageHeader({
  title,
  subtitle,
  breadcrumb,
  actions,
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsxs(
    "header",
    {
      className: cn(
        "mb-[var(--space-section-y)] flex flex-wrap items-start justify-between gap-x-4 gap-y-3",
        className
      ),
      ...rest,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          breadcrumb && /* @__PURE__ */ jsx("div", { className: "mb-1 text-[length:var(--text-meta)] leading-[var(--text-meta--line-height)] text-[var(--color-text-secondary)]", children: breadcrumb }),
          /* @__PURE__ */ jsx("h1", { className: "text-[length:var(--text-page-title)] font-[var(--text-page-title--font-weight)] leading-[var(--text-page-title--line-height)] tracking-tight text-[var(--color-text-primary)]", children: title }),
          subtitle && /* @__PURE__ */ jsx("p", { className: "mt-1 text-[length:var(--text-page-subtitle)] font-[var(--text-page-subtitle--font-weight)] leading-[var(--text-page-subtitle--line-height)] text-[var(--color-text-secondary)]", children: subtitle })
        ] }),
        actions && /* @__PURE__ */ jsx("div", { className: "flex shrink-0 items-center gap-[var(--space-control-gap)]", children: actions })
      ]
    }
  );
}
function SectionHeader({ title, meta, actions, className, ...rest }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn("mb-3 flex items-center justify-between gap-3", className),
      ...rest,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx("h2", { className: "text-[length:var(--text-section-title)] font-[var(--text-section-title--font-weight)] leading-[var(--text-section-title--line-height)] text-[var(--color-text-primary)]", children: title }),
          meta && /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-[length:var(--text-meta)] leading-[var(--text-meta--line-height)] text-[var(--color-text-secondary)]", children: meta })
        ] }),
        actions && /* @__PURE__ */ jsx("div", { className: "flex shrink-0 items-center gap-[var(--space-control-gap)]", children: actions })
      ]
    }
  );
}
function StatCard({
  label,
  value,
  icon,
  delta,
  footnote,
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsxs(Card, { padding: "md", className: cn("min-w-0", className), ...rest, children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3", children: [
      /* @__PURE__ */ jsx("span", { className: "truncate text-[length:var(--text-meta)] font-medium uppercase tracking-wide text-[var(--color-text-secondary)]", children: label }),
      icon && /* @__PURE__ */ jsx("span", { className: "flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[var(--color-surface-2)] text-[var(--color-text-secondary)]", children: icon })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-baseline gap-2", children: [
      /* @__PURE__ */ jsx("span", { className: "text-[length:var(--text-page-title)] font-[var(--text-page-title--font-weight)] leading-[var(--text-page-title--line-height)] tabular-nums text-[var(--color-text-primary)]", children: value }),
      delta
    ] }),
    footnote && /* @__PURE__ */ jsx("p", { className: "mt-1 text-[length:var(--text-meta)] leading-[var(--text-meta--line-height)] text-[var(--color-text-muted)]", children: footnote })
  ] });
}
var SOFT = {
  neutral: "bg-[var(--color-surface-2)] text-[var(--color-text-secondary)]",
  accent: "bg-[var(--color-accent-muted)] text-[var(--color-accent)]",
  success: "bg-[var(--color-success-muted)] text-[var(--color-success)]",
  warning: "bg-[var(--color-warning-muted)] text-[var(--color-warning)]",
  danger: "bg-[var(--color-danger-muted)] text-[var(--color-danger)]",
  info: "bg-[var(--color-brand-purple-muted)] text-[var(--color-brand-purple)]"
};
var SOLID = {
  neutral: "bg-[var(--color-surface-3)] text-[var(--color-text-primary)]",
  accent: "bg-[var(--color-accent)] text-[var(--color-accent-text)]",
  success: "bg-[var(--color-success)] text-white",
  warning: "bg-[var(--color-warning)] text-white",
  danger: "bg-[var(--color-danger)] text-white",
  info: "bg-[var(--color-brand-purple)] text-white"
};
var OUTLINE = {
  neutral: "border border-[var(--color-border)] text-[var(--color-text-secondary)]",
  accent: "border border-[var(--color-accent)] text-[var(--color-accent)]",
  success: "border border-[var(--color-success)] text-[var(--color-success)]",
  warning: "border border-[var(--color-warning)] text-[var(--color-warning)]",
  danger: "border border-[var(--color-danger)] text-[var(--color-danger)]",
  info: "border border-[var(--color-brand-purple)] text-[var(--color-brand-purple)]"
};
var VARIANTS2 = {
  soft: SOFT,
  solid: SOLID,
  outline: OUTLINE
};
var SIZES2 = {
  sm: "px-1.5 py-px text-[11px]",
  md: "px-2 py-0.5 text-xs"
};
function Badge({
  tone = "neutral",
  variant = "soft",
  size = "md",
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-[var(--radius-pill)] font-medium leading-tight",
        VARIANTS2[variant][tone],
        SIZES2[size],
        className
      ),
      ...rest
    }
  );
}
var PILL_SIZES = {
  sm: "h-7 px-2.5 text-xs",
  md: "h-8 px-3 text-[13px]"
};
function FilterPill({
  selected = false,
  count,
  onToggle,
  size = "md",
  className,
  children,
  ...rest
}) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      "aria-pressed": selected,
      onClick: onToggle,
      className: cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-[var(--radius-pill)] border font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]",
        selected ? "border-transparent bg-[var(--color-accent-muted)] text-[var(--color-accent)]" : "border-[var(--color-border)] bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text-primary)]",
        PILL_SIZES[size],
        className
      ),
      ...rest,
      children: [
        children,
        count !== void 0 && /* @__PURE__ */ jsx("span", { className: "text-[11px] tabular-nums opacity-70", children: count })
      ]
    }
  );
}
function FilterGroup({ label, gap, className, style, children, ...rest }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "group",
      className: cn("flex flex-wrap items-center", className),
      style: { gap: gap ?? "var(--space-control-gap)", ...style },
      ...rest,
      children: [
        label && /* @__PURE__ */ jsx("span", { className: "text-[length:var(--text-meta)] font-medium text-[var(--color-text-secondary)]", children: label }),
        children
      ]
    }
  );
}
var APP_LABEL_CLASS = "text-[9px] font-semibold uppercase tracking-[0.36em] text-text-secondary";
var RAIL_BASE = "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-all duration-200";
var RAIL_ACTIVE = "relative bg-surface-2 font-medium text-text-primary before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-accent";
var RAIL_COLLAPSED_BASE = "flex h-9 w-9 items-center justify-center rounded-full transition-all duration-200";
var RAIL_COLLAPSED_ACTIVE = "relative bg-surface-2 text-text-primary before:absolute before:-left-2 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-accent";
var GROUP_BASE = "relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-medium transition-all duration-200";
var GROUP_ACTIVE = "nav-active-rail bg-accent-muted text-accent shadow-card";
var TAB_BASE = "relative flex items-center gap-1.5 px-2 py-2 text-[13px] transition-colors";
var TAB_ACTIVE = "font-medium text-accent after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-accent";
var INACTIVE = "text-text-secondary hover:bg-surface-2 hover:text-text-primary";
var TAB_INACTIVE = "text-text-secondary hover:text-text-primary";
var NavItem = forwardRef(function NavItem2({ icon, label, active = false, level = "group", collapsed = false, className, ...rest }, ref) {
  if (level === "rail" && collapsed) {
    return /* @__PURE__ */ jsx(
      "button",
      {
        ref,
        type: "button",
        title: typeof label === "string" ? label : void 0,
        className: cn(
          RAIL_COLLAPSED_BASE,
          active ? RAIL_COLLAPSED_ACTIVE : INACTIVE,
          className
        ),
        ...rest,
        children: icon
      }
    );
  }
  const base = level === "rail" ? RAIL_BASE : level === "group" ? GROUP_BASE : TAB_BASE;
  const activeClass = level === "rail" ? RAIL_ACTIVE : level === "group" ? GROUP_ACTIVE : TAB_ACTIVE;
  const inactiveClass = level === "tab" ? TAB_INACTIVE : INACTIVE;
  return /* @__PURE__ */ jsxs(
    "button",
    {
      ref,
      type: "button",
      className: cn(base, active ? activeClass : inactiveClass, className),
      ...rest,
      children: [
        icon && /* @__PURE__ */ jsx("span", { className: "flex shrink-0 items-center justify-center", children: icon }),
        /* @__PURE__ */ jsx("span", { className: "truncate", children: label })
      ]
    }
  );
});
var ALIGN = {
  left: "text-left",
  center: "text-center",
  right: "text-right"
};
function cellValue(row, col, index) {
  if (col.render) return col.render(row, index);
  const value = row[col.key];
  return value;
}
function DataTable({
  columns,
  rows,
  rowKey,
  onRowClick,
  dense = false,
  sort,
  onSortChange,
  vAlign = "middle",
  className
}) {
  const cellPad = dense ? "px-3 py-1.5" : "px-3 py-[var(--space-row-y)]";
  const headPad = dense ? "px-3 py-2" : "px-3 py-2.5";
  function nextSort(key) {
    if (sort?.key === key && sort.direction === "asc") return { key, direction: "desc" };
    return { key, direction: "asc" };
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "overflow-x-auto rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-1)] shadow-card",
        className
      ),
      children: /* @__PURE__ */ jsxs("table", { className: "w-full border-collapse text-[length:var(--text-body)] leading-[var(--text-body--line-height)]", children: [
        /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsx("tr", { className: "border-b border-[var(--color-border)] bg-[var(--color-surface-0)]", children: columns.map((col) => {
          const content = /* @__PURE__ */ jsxs(Fragment, { children: [
            col.label,
            sort?.key === col.key && /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "ml-1 inline-block text-[10px]", children: sort.direction === "asc" ? "\u25B4" : "\u25BE" })
          ] });
          return /* @__PURE__ */ jsx(
            "th",
            {
              scope: "col",
              style: { width: col.width },
              "aria-sort": sort?.key === col.key ? sort.direction === "asc" ? "ascending" : "descending" : void 0,
              className: cn(
                "whitespace-nowrap text-[length:var(--text-table-header)] font-[var(--text-table-header--font-weight)] leading-[var(--text-table-header--line-height)] text-[var(--color-text-secondary)]",
                headPad,
                ALIGN[col.align ?? "left"]
              ),
              children: onSortChange ? /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  onClick: () => onSortChange(nextSort(col.key)),
                  className: "inline-flex items-center font-[inherit] text-inherit hover:text-[var(--color-text-primary)]",
                  children: content
                }
              ) : content
            },
            col.key
          );
        }) }) }),
        /* @__PURE__ */ jsx("tbody", { children: rows.map((row, i) => /* @__PURE__ */ jsx(
          "tr",
          {
            onClick: onRowClick ? () => onRowClick(row, i) : void 0,
            tabIndex: onRowClick ? 0 : void 0,
            onKeyDown: onRowClick ? (event) => {
              if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                onRowClick(row, i);
              }
            } : void 0,
            className: cn(
              "border-b border-[var(--color-border-subtle)] last:border-b-0",
              onRowClick && "cursor-pointer transition-colors hover:bg-[var(--color-surface-2)] focus-visible:bg-[var(--color-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent)]"
            ),
            children: columns.map((col) => /* @__PURE__ */ jsx(
              "td",
              {
                className: cn(
                  "text-[var(--color-text-primary)]",
                  vAlign === "top" && "align-top",
                  cellPad,
                  ALIGN[col.align ?? "left"]
                ),
                children: cellValue(row, col, i)
              },
              col.key
            ))
          },
          rowKey ? rowKey(row, i) : i
        )) })
      ] })
    }
  );
}
function WorkforcePulse({
  title,
  subtitle,
  icon,
  active = false,
  metrics,
  action,
  error,
  compact = false,
  loading = false,
  className,
  children,
  ...rest
}) {
  const chip = /* @__PURE__ */ jsxs("span", { className: "relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--color-accent-muted)] text-[var(--color-accent)]", children: [
    icon ?? /* @__PURE__ */ jsx(Bot, { size: 17, strokeWidth: ICON_STROKE }),
    active && /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": true,
        className: "absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-pulse rounded-full border-2 border-[var(--color-surface-1)] bg-[var(--color-success)]"
      }
    )
  ] });
  if (compact) {
    if (loading) {
      return /* @__PURE__ */ jsxs(
        Card,
        {
          padding: "none",
          "aria-busy": "true",
          className: cn("flex h-[62px] min-w-[270px] items-center gap-3 px-3.5 py-2.5", className),
          ...rest,
          children: [
            /* @__PURE__ */ jsx(Skeleton, { w: 36, h: 36, radius: "lg", className: "shrink-0" }),
            /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1 space-y-1.5", children: [
              /* @__PURE__ */ jsx(Skeleton, { w: 112, h: 12 }),
              /* @__PURE__ */ jsx(Skeleton, { w: 160, h: 10 })
            ] })
          ]
        }
      );
    }
    return /* @__PURE__ */ jsxs(
      Card,
      {
        padding: "none",
        className: cn("flex h-[62px] min-w-[270px] items-center gap-3 px-3.5 py-2.5", className),
        ...rest,
        children: [
          chip,
          /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx("p", { className: "text-xs font-semibold text-[var(--color-text-primary)]", children: title }),
              subtitle && /* @__PURE__ */ jsx("span", { className: "text-[11px] text-[var(--color-text-secondary)]", children: subtitle })
            ] }),
            children && /* @__PURE__ */ jsx("div", { className: "mt-1 flex items-center gap-2 text-[11px] text-[var(--color-text-secondary)]", children })
          ] }),
          action
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs(Card, { padding: "none", className: cn("overflow-hidden", className), ...rest, children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-3", children: [
        chip,
        /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx("h2", { className: "text-[13px] font-semibold text-[var(--color-text-primary)]", children: title }),
          subtitle && /* @__PURE__ */ jsx("p", { className: "mt-0.5 truncate text-[11px] text-[var(--color-text-secondary)]", children: subtitle })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-x-6 gap-y-2", children: [
        metrics?.map((m) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5 whitespace-nowrap", children: [
          m.icon,
          /* @__PURE__ */ jsx("span", { className: "text-[13px] font-semibold tabular-nums text-[var(--color-text-primary)]", children: m.value }),
          /* @__PURE__ */ jsx("span", { className: "text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-secondary)]", children: m.label })
        ] }, m.key)),
        action && /* @__PURE__ */ jsx("span", { className: "sm:ml-1 sm:border-l sm:border-[var(--color-border)] sm:pl-4", children: action })
      ] })
    ] }),
    error && /* @__PURE__ */ jsx("p", { className: "px-5 pb-4 text-xs text-[var(--color-danger)]", children: error })
  ] });
}
var MODULE_KEY = "omni.sidebar.pinned";
var SHELL_KEY = "omni.nav.shell";
var SYNC_EVENT = "omni:nav-layout";
function read(key) {
  if (typeof window === "undefined") return "expanded";
  try {
    return window.localStorage.getItem(key) === "collapsed" ? "collapsed" : "expanded";
  } catch {
    return "expanded";
  }
}
function write(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
  }
  window.dispatchEvent(new CustomEvent(SYNC_EVENT));
}
function subscribe(onChange) {
  window.addEventListener(SYNC_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(SYNC_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}
function snapshot() {
  return `${read(SHELL_KEY)}|${read(MODULE_KEY)}`;
}
function serverSnapshot() {
  return "expanded|expanded";
}
function useNavLayout() {
  const value = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  const [shell, module] = value.split("|");
  const shellCollapsed = shell === "collapsed";
  const moduleCollapsed = module === "collapsed";
  const toggleShell = useCallback(() => {
    write(SHELL_KEY, read(SHELL_KEY) === "collapsed" ? "expanded" : "collapsed");
  }, []);
  const toggleModule = useCallback(() => {
    write(MODULE_KEY, read(MODULE_KEY) === "collapsed" ? "expanded" : "collapsed");
  }, []);
  const toggleFocus = useCallback(() => {
    const next = read(SHELL_KEY) === "collapsed" && read(MODULE_KEY) === "collapsed" ? "expanded" : "collapsed";
    write(SHELL_KEY, next);
    write(MODULE_KEY, next);
  }, []);
  return {
    shellCollapsed,
    moduleCollapsed,
    focusMode: shellCollapsed && moduleCollapsed,
    toggleShell,
    toggleModule,
    toggleFocus
  };
}

export { APP_LABEL_CLASS, Badge, Button, Card, CardHeader, CardTitle, DataTable, EmptyState, ErrorState, FilterGroup, FilterPill, Modal, NavItem, OmniLoadingScreen, PageError, PageHeader, PageLoading, SectionHeader, Skeleton, SkeletonRow, SkeletonText, StatCard, Tabs, ToastProvider as Toast, ToastProvider, WorkforcePulse, cn, useNavLayout, useToast };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map